import type { Source } from "@tandemdiabetes/sugarmate-api-react-client"
import { SourceType } from "@tandemdiabetes/sugarmate-api-react-client"
import {
  CGMMenuItem,
  CGMMenuItemIconButton,
  CGMMenuItemLabel,
  CGMMenuItemSubtext,
  CGMMenuItemText,
  TrashCanOutlineIcon,
  useToasterContext,
} from "@tandemdiabetes/sugarmate-react-components"
import type { MouseEventHandler } from "react"
import {
  useCallback,
  useState,
  useMemo,
} from "react"

import { RemoveDialog } from "src/components"
import {
  useOverlayLoader,
  usePolyglot,
} from "src/contexts"
import { useDeleteSource } from "src/hooks"
import {
  RemoveSourceErrorToast,
  RemoveSourceSuccessToast,
} from "src/toasts"

const polyglotPrefix = "pages.settings.data_source."

interface Props {
  source: Source;
}

// TODO make CGM button NOT a button
export function DataSourceComponent(props: Props) {
  const { source } = props

  const polyglot = usePolyglot()
  const { showToast } = useToasterContext()
  const { setIsOverlayLoaderVisible } = useOverlayLoader()
  const sourceId = source.id
  const deleteSource = useDeleteSource()

  const [
    removeDialogOpen,
    setRemoveDialogOpen,
  ] = useState<boolean>(false)

  const onRemoveClick: MouseEventHandler<HTMLButtonElement> =
    useCallback(
      () => {
        void (async () => {
          setIsOverlayLoaderVisible(true)

          try {
            if (!deleteSource) {
              throw new Error("cannot delete source")
            }

            await deleteSource(sourceId)
            showToast(
              "remove-nightscout-source-success-toast",
              RemoveSourceSuccessToast,
            )
          } catch {
            showToast(
              "remove-nightscout-source-error-toast",
              RemoveSourceErrorToast,
            )
          }

          setIsOverlayLoaderVisible(false)
        })()
      },
      [
        sourceId,
        deleteSource,
        showToast,
        setIsOverlayLoaderVisible,
      ],
    )

  const onClick = useCallback(
    () => {
      setRemoveDialogOpen(true)
    },
    [setRemoveDialogOpen],
  )

  const sourceType = useMemo(
    () => {
      if (source.source_type === SourceType.Nightscout) {
        return "nightscout"
      }

      if (source.source_type === SourceType.Tandem) {
        return "tandem"
      }

      if (source.source_type === SourceType.Dexcom) {
        return "dexcom"
      }

      return undefined
    },
    [source.source_type],
  )

  const subtext = useMemo(
    () => {
      if (source.source_type === SourceType.Nightscout && source.url) {
        return source.url
      }

      const connection = source.error
        ? polyglot.t(`${polyglotPrefix}component.disconnected`)
        : polyglot.t(`${polyglotPrefix}component.connected`)

      const validity = source.error
        ? polyglot.t(`${polyglotPrefix}component.invalid`)
        : polyglot.t(`${polyglotPrefix}component.valid`)

      return polyglot.t(
        `${polyglotPrefix}component.subtext`,
        {
          connection,
          validity,
        },
      )
    },
    [
      polyglot,
      source,
    ],
  )

  if (sourceType === undefined) {
    return null
  }

  return (
    <>
      <CGMMenuItem>
        <CGMMenuItemText
          id={`${sourceType}-data-source`}
        >
          <CGMMenuItemLabel
            source={sourceType}
            id="connected-data-source-name"
          />
          <CGMMenuItemSubtext id="connection-activity-of-data-source">
            {subtext}
          </CGMMenuItemSubtext>
        </CGMMenuItemText>
        <CGMMenuItemIconButton onClick={onClick}>
          <TrashCanOutlineIcon id="remove-data-source-button" />
        </CGMMenuItemIconButton>
      </CGMMenuItem>
      <RemoveDialog
        open={removeDialogOpen}
        setOpen={setRemoveDialogOpen}
        remove={onRemoveClick}
        title={polyglot.t(`${polyglotPrefix}delete_dialog.title`)}
        removeCta={polyglot.t(`${polyglotPrefix}delete_dialog.delete_cta`)}
      >
        {polyglot.t(`${polyglotPrefix}delete_dialog.description`)}
      </RemoveDialog>
    </>
  )
}
